import { AnyAction } from 'redux';
import {
  GlobalActions,
  InitializeShopPayload,
  RoleDeterminedPayload,
  Settings
} from '@ps20-frontend/shop-integration-redux';
import {
  CHANGE_SEARCH_DIALOG_STATE,
  isSearchDialogReducerAction,
  SearchDialogContainerState,
  searchDialogReducer
} from '@ps20-frontend/gui-components';
import { IAppState } from '@ps20-frontend/apps/checkout-store';
import {
  App,
  areAppTypesEquivalent,
  FeatureToggles,
  isVwgV2,
  Regexps,
  Rolle,
} from '@ps20-frontend/frontend-models';
import { UnderwriterFragebogenCheckout } from '@ps20-frontend/uw';
import cloneDeep from 'lodash/cloneDeep';
import { CheckoutActions } from '../checkout.actions';
import { Dokument } from '../model/dokumente.enum';
import {
  ANTRAG_EINREICHEN_POLLING_EVENTS,
  CAP_UPLOAD_EVENTS,
} from '../sagas/pollingHelper/polling-events';
import { CheckoutPersonenSearchListItem } from '../model/checkoutPersonenSearchListItem';
import { EleUploadedFile, FileStatus } from '../model';
import { FragebogenType } from '../model/fragebogenType.enum';

export interface TransientState extends Rolle {
  tarifierungIsInProgress: boolean;
  einreichenButtonClicked: boolean;
  infoPaketDruckIsInProgress: boolean;
  beiblattDruckIsInProgress: boolean;
  pkbDruckIsInProgress: boolean;
  jagdbestaetigungDruckIsInProgress: boolean;
  weweDruckIsInProgress: boolean;
  einreichenIsInProgress: boolean;
  testdokumenteDownloadIsInProgress: boolean;
  disableEinreichenUw?: boolean;
  featureToggles: FeatureToggles;
  regexps: Regexps;
  personenSuchdialog: SearchDialogContainerState<CheckoutPersonenSearchListItem>;
  zurueckZumShopModalOpen: boolean;
  immoModalOpened: boolean;
  onboardingVideo?: boolean;
  accessToken?: string;
  isSdwSession?: boolean;
  permasession?: boolean;
  einreichenSimulieren?: boolean;
  showRabattHinweis: boolean;
  vorbelegungIsInProgress: boolean;
  amisCRMRquestInProgress: boolean;
  requestFailed: boolean;
  validationTriggered: boolean;
  settings?: Settings;
  fileDeletion: FileDeletion[];
  fileModificationInProgress: boolean;
  elementarfragebogenLastSuccessfullOperation: EleUploadedFile[];
  programmteilnahmeResponseReceived?: boolean;
  fragebogenValidationRevealed: FragebogenValidationRevealed[];
}

export interface FileDeletion {
  shouldShowSuccessMessage: boolean;
  shouldShowErrorMessage: boolean;
  fileUploaderId: string;
}

export interface FragebogenValidationRevealed {
  angebotsId: string;
  fragebogenType: FragebogenType;
}

export const transientStateReducer = function (
  sectionState: TransientState = null,
  action: AnyAction,
  state: IAppState,
): TransientState {
  switch (action.type) {
    case CheckoutActions.ELEMENTARFRAGEBOGEN_FILES_UPLOADED_SUCCESSFULLY: {
      const filesIds: string[] = action.filesIds;
      const files = filesIds?.map(c => {
        return { id: c, status: FileStatus.UPLOADED } as EleUploadedFile;
      });
      return {
        ...sectionState,
        elementarfragebogenLastSuccessfullOperation: files,
      };
    }
    case CheckoutActions.ELEMENTARFRAGEBOGEN_FILE_DELETED_SUCCESSFULLY: {
      const newState = cloneDeep(sectionState);
      const payload = action.payload;
      newState.elementarfragebogenLastSuccessfullOperation = [
        {
          id: payload.deletedFileId,
          status: FileStatus.DELETED,
          angebotsId: payload.angebotsId,
        } as EleUploadedFile,
      ];
      return newState;
    }
    case CheckoutActions.REMOVE_ELEMENTARFRAGEBOGEN_OPERATIONS_STATUS: {
      const newState = cloneDeep(sectionState);
      newState.elementarfragebogenLastSuccessfullOperation = [];
      return newState;
    }
    case GlobalActions.TARIFIERUNG_HAS_STARTED: {
      return { ...sectionState, tarifierungIsInProgress: true };
    }
    case GlobalActions.TARIFIERUNG_HAS_ENDED: {
      return { ...sectionState, tarifierungIsInProgress: false };
    }
    case CheckoutActions.EINREICHEN_BUTTON_CLICKED: {
      return { ...sectionState, einreichenButtonClicked: true, validationTriggered: true };
    }
    case CheckoutActions.VALIDIERE_INPUT_COMPLECTION_CARD: {
      return { ...sectionState, validationTriggered: true };
    }
    case CheckoutActions.DRUCKEN_LINK_CLICKED: {
      return {
        ...sectionState,
        infoPaketDruckIsInProgress: action.payload === Dokument.INFOPAKET,
        beiblattDruckIsInProgress:
          action.payload === (Dokument.BEIBLATT_MIT_SEPA || Dokument.BEIBLATT_OHNE_SEPA),
        pkbDruckIsInProgress: action.payload === Dokument.PKB,
        jagdbestaetigungDruckIsInProgress: action.payload === Dokument.JAGDBESTAETIGUNG,
        weweDruckIsInProgress: action.payload === Dokument.WEWE,
      };
    }
    case CheckoutActions.INFOPAKET_DRUCKEN_SUCCESS: {
      return { ...sectionState, infoPaketDruckIsInProgress: false };
    }
    case CheckoutActions.INFOPAKET_DRUCKEN_ERROR: {
      return {
        ...sectionState,
        infoPaketDruckIsInProgress: false,
        testdokumenteDownloadIsInProgress: false,
      };
    }
    case CheckoutActions.BEIBLATT_DRUCKEN_SUCCESS:
    case CheckoutActions.BEIBLATT_DRUCKEN_ERROR: {
      return { ...sectionState, beiblattDruckIsInProgress: false };
    }
    case CheckoutActions.PKB_DRUCKEN_SUCCESS:
    case CheckoutActions.PKB_DRUCKEN_ERROR: {
      return {
        ...sectionState,
        pkbDruckIsInProgress: false,
        testdokumenteDownloadIsInProgress: false,
      };
    }
    case CheckoutActions.TESTDOKUMENTE_DOWNLOAD_CLICKED: {
      return { ...sectionState, testdokumenteDownloadIsInProgress: true };
    }
    case CheckoutActions.WEWE_DRUCKEN_SUCCESS:
    case CheckoutActions.WEWE_DRUCKEN_ERROR: {
      return { ...sectionState, weweDruckIsInProgress: false };
    }
    case CheckoutActions.JAGDBESTAETIGUNG_DRUCKEN_SUCCESS:
    case CheckoutActions.JAGDBESTAETIGUNG_DRUCKEN_ERROR: {
      return { ...sectionState, jagdbestaetigungDruckIsInProgress: false };
    }
    case CheckoutActions.CAP_ANTRAG_EINREICHEN:
    case CheckoutActions.ANTRAG_EINREICHEN: {
      return { ...sectionState, einreichenIsInProgress: true };
    }
    case ANTRAG_EINREICHEN_POLLING_EVENTS.ERROR:
    case CAP_UPLOAD_EVENTS.UPLOAD_ERROR:
    case CAP_UPLOAD_EVENTS.UPLOAD_SUCCESSFUL:
    case ANTRAG_EINREICHEN_POLLING_EVENTS.SUCCESSFUL: {
      return { ...sectionState, einreichenIsInProgress: false };
    }
    case GlobalActions.FEATURE_TOGGLE_RECEIVED: {
      const actionPayload: FeatureToggles = action.payload;
      return { ...sectionState, featureToggles: actionPayload };
    }
    case GlobalActions.ELEMENTARFRAGEBOGEN_PROVIDED: {
      const actionPayload: UnderwriterFragebogenCheckout = action.payload;
      return { ...sectionState, disableEinreichenUw: actionPayload.disableSubmission };
    }
    case GlobalActions.REGEXPS_RECEIVED: {
      const actionPayload: Regexps = action.payload;
      return { ...sectionState, regexps: actionPayload };
    }
    case CheckoutActions.OPEN_IMMOBILIEN_MODAL: {
      return { ...sectionState, immoModalOpened: true };
    }
    case CheckoutActions.CLOSE_IMMOBILIEN_MODAL: {
      return { ...sectionState, immoModalOpened: false };
    }
    case GlobalActions.SET_ACCESS_TOKEN: {
      return { ...sectionState, accessToken: action.payload };
    }
    case GlobalActions.SHOP_INIT: {
      const actionPayload: InitializeShopPayload = action.payload;
      return {
        ...sectionState,
        isSdwSession: ![null, undefined].includes(actionPayload.sdw?.partyId),
        permasession: actionPayload.permasession,
      };
    }
    case GlobalActions.NAVIGATION_COMPLETED: {
      return {
        ...sectionState,
        onboardingVideo:
          action.payload.appId === App.SHOP_CHECKOUT && checkIfEmailIsAvailable(state),
        validationTriggered: false,
        fragebogenValidationRevealed: [],
        einreichenSimulieren: false,
      };
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_EMAIL_CHANGED: {
      return {
        ...sectionState,
        onboardingVideo: action.payload.value !== '',
      };
    }
    case CheckoutActions.ZURUECK_ZUM_SHOP_MODAL_OPEN: {
      return { ...sectionState, zurueckZumShopModalOpen: true };
    }
    case CheckoutActions.ZURUECK_ZUM_SHOP_MODAL_CLOSE: {
      return { ...sectionState, zurueckZumShopModalOpen: false };
    }
    case CheckoutActions.EINREICHEN_SIMULIEREN_CHANGED: {
      return { ...sectionState, einreichenSimulieren: action.payload };
    }
    case CHANGE_SEARCH_DIALOG_STATE: {
      if (isSearchDialogReducerAction<CheckoutPersonenSearchListItem>(action)) {
        return {
          ...sectionState,
          personenSuchdialog: searchDialogReducer(
            sectionState && sectionState.personenSuchdialog,
            action,
          ),
        };
      }
      return sectionState;
    }
    case GlobalActions.ROLE_DETERMINED: {
      const actionPayload: RoleDeterminedPayload = action.payload;
      return { ...sectionState, role: actionPayload.role };
    }
    case GlobalActions.PRODUKT_DELETED:
      return { ...sectionState, showRabattHinweis: false };
    case GlobalActions.RABATT_HINWEIS_CHANGED: {
      return { ...sectionState, showRabattHinweis: action.payload };
    }
    case GlobalActions.VORBELEGUNG_REQUESTED: {
      return { ...sectionState, vorbelegungIsInProgress: true };
    }
    case GlobalActions.VORBELEGUNG_FINISHED: {
      return { ...sectionState, vorbelegungIsInProgress: false };
    }
    case GlobalActions.AMIS_CRM_IMMOBILIE_REQUESTED: {
      return {
        ...sectionState,
        amisCRMRquestInProgress: true,
        requestFailed: false,
      };
    }
    case GlobalActions.AMIS_IMMOBILIEN_TRANSFORMED: {
      return { ...sectionState, amisCRMRquestInProgress: false };
    }
    case GlobalActions.AMIS_CRM_IMMOBILIE_REQUEST_FAILED: {
      return {
        ...sectionState,
        amisCRMRquestInProgress: false,
        requestFailed: true,
      };
    }
    case GlobalActions.SETTINGS_RECEIVED: {
      return {
        ...sectionState,
        settings: action.payload,
      };
    }
    case CheckoutActions.FRAGEBOGEN_VALIDATION_REVEALED: {
      const fragebogenValidationRevealed = (
        sectionState.fragebogenValidationRevealed ?? []
      ).filter(
        revealed =>
          revealed.angebotsId !== action.payload.angebotsId ||
          revealed.fragebogenType !== action.payload.fragebogenType,
      );
      fragebogenValidationRevealed.push({
        angebotsId: action.payload.angebotsId,
        fragebogenType: action.payload.fragebogenType,
      });
      return {
        ...sectionState,
        fragebogenValidationRevealed,
      };
    }
    case CheckoutActions.FILE_DELETION_REQUESTED: {
      const fileDeletion = sectionState.fileDeletion.filter(
        fd => fd.fileUploaderId !== action.payload.fileUploaderId,
      );
      fileDeletion.push({
        fileUploaderId: action.payload.fileUploaderId,
        shouldShowErrorMessage: false,
        shouldShowSuccessMessage: false,
      });
      return {
        ...sectionState,
        fileDeletion,
      };
    }
    case CheckoutActions.FILE_DELETED: {
      return {
        ...sectionState,
        fileDeletion: sectionState.fileDeletion.map(fd => {
          if (fd.fileUploaderId === action.payload.fileUploaderId) {
            fd.shouldShowSuccessMessage = true;
          }
          return fd;
        }),
      };
    }
    case CheckoutActions.FILE_DELETION_FAILED: {
      return {
        ...sectionState,
        fileDeletion: sectionState.fileDeletion.map(fd => {
          if (fd.fileUploaderId === action.payload.fileUploaderId) {
            fd.shouldShowErrorMessage = true;
          }
          return fd;
        }),
      };
    }
    case CheckoutActions.CLOSE_FILE_DELETION_MESSAGES: {
      return {
        ...sectionState,
        fileDeletion: sectionState.fileDeletion.map(fd => {
          if (fd.fileUploaderId === action.payload.fileUploaderId) {
            fd.shouldShowSuccessMessage = false;
            fd.shouldShowErrorMessage = false;
          }
          return fd;
        }),
      };
    }
    case CheckoutActions.FILE_MODIFICATION_IN_PROGRESS: {
      return {
        ...sectionState,
        fileModificationInProgress: true,
      };
    }
    case CheckoutActions.FILE_MODIFICATION_FINISHED: {
      return {
        ...sectionState,
        fileModificationInProgress: false,
      };
    }
    case CheckoutActions.PROGRAMMTEILNAHME_SUCCESS:
    case CheckoutActions.PROGRAMMTEILNAHME_FAILED: {
      return {
        ...sectionState,
        programmteilnahmeResponseReceived: true,
      };
    }
    default:
      return sectionState;
  }
};

function checkIfEmailIsAvailable(state: IAppState): boolean {
  const emailAvailable =
    state &&
    state.versicherungsnehmer &&
    state.versicherungsnehmer.email &&
    state.versicherungsnehmer.email.wert !== '';

  if (!emailAvailable) {
    return false;
  }

  // Temporary: Disable onboarding video for TKV-only carts
  // TODO: Remove when TKV explainer video is updated
  const produkte = state.produkte || [];
  if (produkte.length === 0) {
    return true;
  }

  const hasOnlyTkvOrVwgV2 = produkte.every(
    produkt => areAppTypesEquivalent(produkt.appId, App.TKV) || isVwgV2(produkt.appId)
  );

  return !hasOnlyTkvOrVwgV2;
}
